import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { observable, action, toJS } from 'mobx';
import { isEqual, omit, pick } from 'lodash';
import {
  BorderedTab,
  BorderedTabs,
  Box,
  Button,
  Callout,
  Collapse,
  Flex,
  FlexColumn,
  Icon,
  Spinner,
  Switch,
  Tag,
  Text
} from 'core/components';
import { Field, FormDialog, InputGroup, Select, Switch as FormSwitch, TextArea } from 'core/form';
import { fields } from 'app/forms/config/mcpServer';
import { openMcpOAuthPopup } from 'app/util/mcpOAuthPopup';
import { MCP_AUTH_TYPE } from '@kentik/ui-shared/kaizen/constants';
import CopyToClipboardButton from 'app/components/CopyToClipboardButton';
import CreateMcpCredentialDialog from './CreateMcpCredentialDialog';

const McpServerToolsList = observer(({ tools, enabledToolNames, onToggleTool }) => {
  if (!tools || tools.length === 0) {
    return (
      <FlexColumn alignItems="center" justifyContent="center" p={4}>
        <Text muted>No tools discovered. Enter a URL and click Discover to find available tools.</Text>
      </FlexColumn>
    );
  }

  return (
    <FlexColumn gap="4px" px={2} py={1} overflow="auto">
      <Flex justifyContent="space-between" alignItems="center" mb={1}>
        <Text small muted>
          {enabledToolNames.size} of {tools.length} tools enabled
        </Text>
        <Flex gap={1}>
          <Button small minimal onClick={() => tools.forEach((t) => onToggleTool(t.name, true))}>
            Enable All
          </Button>
          <Button small minimal onClick={() => tools.forEach((t) => onToggleTool(t.name, false))}>
            Disable All
          </Button>
        </Flex>
      </Flex>
      {tools.map((tool) => (
        <Flex
          key={tool.name}
          alignItems="flex-start"
          p={1}
          borderRadius="4px"
          style={{ border: '1px solid var(--border-color, #e1e1e1)' }}
        >
          <Switch
            checked={enabledToolNames.has(tool.name)}
            onChange={() => onToggleTool(tool.name, !enabledToolNames.has(tool.name))}
            alignIndicator="left"
            style={{ marginBottom: 0 }}
          />
          <FlexColumn ml={1} flex={1} minWidth={0}>
            <Text fontWeight={600}>{tool.name}</Text>
            <Text small muted style={{ whiteSpace: 'pre-wrap' }}>
              {tool.description}
            </Text>
          </FlexColumn>
        </Flex>
      ))}
    </FlexColumn>
  );
});

const canonicalScopes = (scopes) => [...new Set((scopes || '').split(/\s+/).filter(Boolean))].sort().join(' ');

const SCOPE_CHANGE_WARNING =
  'Changing the OAuth scopes disconnects every user from this server. Everyone will need to reconnect. Continue?';

// Provider endpoints come only from the server's own discovery, so Save never sends them.
const DISCOVERED_ENDPOINT_FIELDS = ['oauth_auth_url', 'oauth_token_url', 'oauth_revocation_url'];
const CONFIGURED_OAUTH_FIELDS = ['oauth_client_id', ...DISCOVERED_ENDPOINT_FIELDS, 'oauth_scopes'];

@inject('$credentials', '$auth', '$mcpServers')
@observer
class McpServerDialog extends Component {
  @observable discoveredTools = [];

  @observable enabledToolNames = new Set();

  @observable isDiscovering = false;

  @observable discoverError = null;

  @observable showCredentialDialog = false;

  @observable credentialDialogModel = null;

  @observable authType = MCP_AUTH_TYPE.HEADERS;

  @observable oauthDiscovered = null;

  @observable showAdvancedOAuth = false;

  @observable urlValue = '';

  @observable isConnecting = false;

  @observable oauthConnected = false;

  @observable authorizeUrl = null;

  @observable skippedTools = [];

  @observable provisionalServerId = null;

  @observable serverRedirectUri = '';

  @observable.ref form;

  get scopesChanged() {
    const { model } = this.props;

    return (
      !!this.form &&
      !model.isNew &&
      model.get('auth_type') === MCP_AUTH_TYPE.OAUTH &&
      canonicalScopes(this.form.getValue('oauth_scopes')) !== canonicalScopes(model.get('oauth_scopes'))
    );
  }

  get hasChanges() {
    const { model } = this.props;
    const savedToolNames = (model.get('enabled_tools') || []).map((tool) => tool.name);

    return (
      model.isNew ||
      this.provisionalServerId !== null ||
      Boolean(this.form?.dirty) ||
      this.discoveredTools !== this.savedDiscoveredTools ||
      this.enabledToolNames.size !== savedToolNames.length ||
      savedToolNames.some((name) => !this.enabledToolNames.has(name))
    );
  }

  componentDidMount() {
    const { model, $mcpServers } = this.props;

    if (model && !model.isNew) {
      const discovered = model.get('discovered_tools') || [];
      const enabled = model.get('enabled_tools') || [];

      this.discoveredTools = discovered;
      this.savedDiscoveredTools = this.discoveredTools;
      this.enabledToolNames = new Set(enabled.map((t) => t.name));
      this.authType = model.get('auth_type');

      if (this.authType === MCP_AUTH_TYPE.OAUTH) {
        this.oauthDiscovered = model.get('oauth_auth_url')
          ? {
              oauth_auth_url: model.get('oauth_auth_url'),
              oauth_token_url: model.get('oauth_token_url'),
              oauth_revocation_url: model.get('oauth_revocation_url'),
              oauth_scopes: model.get('oauth_scopes'),
              resource: model.get('oauth_resource')
            }
          : null;

        $mcpServers
          .fetchOAuthStatus(model.get('id'))
          .then(
            action((status) => {
              this.oauthConnected = status?.connected || false;
              this.serverRedirectUri = status?.redirect_uri || '';
            })
          )
          .catch(
            action((err) => {
              this.discoverError = err.message || 'Could not read your connection status';
            })
          );
      }
    }
  }

  // --- Discover ---

  @action
  handleDiscover = () => {
    const url = this.form.getValue('url');

    if (!url) {
      this.discoverError = 'Enter a URL first';

      return;
    }

    this.isDiscovering = true;
    this.discoverError = null;

    const { model, $mcpServers } = this.props;
    const server_id = model && !model.isNew ? model.get('id') : undefined;

    $mcpServers
      .discoverTools({
        url,
        credential_name: this.form.getValue('credential_name') || undefined,
        auth_type: this.authType,
        server_id
      })
      .then(
        action((response) => {
          this.isDiscovering = false;

          if (response.oauth_required && response.needs_connect) {
            this.discoverError = 'Connect your account first to discover tools.';

            return;
          }

          if (response.oauth_required && response.oauth_config) {
            this.applyDiscoveredOAuth(response.oauth_config);

            return;
          }

          const tools = response?.tools || [];
          const previouslyEnabled = this.enabledToolNames;
          this.setDiscoveredTools(tools);
          this.skippedTools = response?.skipped_tools || [];
          this.enabledToolNames =
            previouslyEnabled.size > 0
              ? new Set(tools.filter((t) => previouslyEnabled.has(t.name)).map((t) => t.name))
              : new Set(tools.map((t) => t.name));
        })
      )
      .catch(
        action((err) => {
          this.discoverError = err.message || 'Failed to connect to MCP server';
          this.isDiscovering = false;
        })
      );
  };

  @action
  applyDiscoveredOAuth = (config) => {
    this.oauthDiscovered = config;
    this.authType = MCP_AUTH_TYPE.OAUTH;
    this.form.setValue('auth_type', MCP_AUTH_TYPE.OAUTH);

    ['oauth_auth_url', 'oauth_token_url', 'oauth_revocation_url', 'oauth_scopes'].forEach((fieldName) => {
      if (config[fieldName]) {
        this.form.setValue(fieldName, config[fieldName]);
      }
    });
  };

  // --- OAuth connect ---

  @action
  handleConnect = () => {
    const { model } = this.props;

    if (!model.isNew && this.form.getValue('url') !== model.get('url')) {
      this.discoverError = 'Save the URL change before connecting.';

      return;
    }

    if (this.scopesChanged) {
      this.discoverError = 'Save the scope change before connecting.';

      return;
    }

    this.isConnecting = true;
    this.discoverError = null;
    this.authorizeUrl = null;

    this.ensureServerSaved()
      .then((serverId) => this.configureOAuth(serverId).then(() => serverId))
      .then((serverId) =>
        openMcpOAuthPopup(serverId, {
          onBlocked: action((url) => {
            this.authorizeUrl = url;
          })
        })
      )
      .then(
        action((result) => {
          this.isConnecting = false;
          this.authorizeUrl = null;

          if (result.connected) {
            this.oauthConnected = true;
            this.discoverToolsAfterConnect();
          }
        })
      )
      .catch(
        action((err) => {
          this.isConnecting = false;
          this.authorizeUrl = null;

          if (!err.cancelled) {
            this.discoverError = err.message || 'Connection failed';
          }
        })
      );
  };

  // The OAuth state is keyed on a saved server, so the record must exist before the popup opens.
  ensureServerSaved = () =>
    Promise.resolve().then(() => {
      const { model } = this.props;
      this.form.validate();

      if (!this.form.valid) {
        throw new Error('Please fill in all required fields before connecting.');
      }

      if (!model.isNew) {
        return model.get('id');
      }

      const values = this.form.getValues();

      return model
        .save(
          {
            name: values.name,
            description: values.description,
            url: values.url,
            credential_name: values.credential_name,
            type: values.type,
            enabled: false,
            discovered_tools: this.discoveredTools,
            enabled_tools: this.buildEnabledTools()
          },
          { toast: false }
        )
        .then(() => {
          const id = model.get('id');
          // uniqueMcpServerName reads the id off the form to skip this server.
          this.form.setValue('id', id);
          this.provisionalServerId = id;

          return id;
        });
    });

  // The server rediscovers its endpoints and registers a public client when it needs one.
  configureOAuth = (serverId) => {
    const { $mcpServers, model } = this.props;
    const scopes = this.form.getValue('oauth_scopes');
    const clientId = this.form.getValue('oauth_client_id');
    const firstSetup = model.get('auth_type') !== MCP_AUTH_TYPE.OAUTH;
    const payload = {
      // First setup takes the provider's scopes when the field is empty; a configured server keeps the field's.
      oauth_scopes: firstSetup ? scopes || undefined : scopes || null,
      client_id: clientId && clientId !== model.get('oauth_client_id') ? clientId : undefined
    };

    return $mcpServers.configureServerOAuth(serverId, payload).then(
      action((updated) => {
        model.set(pick(updated, CONFIGURED_OAUTH_FIELDS));
        CONFIGURED_OAUTH_FIELDS.forEach((name) => this.form.getField(name).init(updated?.[name] || ''));
      })
    );
  };

  @action
  discoverToolsAfterConnect = () => {
    const { model, $mcpServers } = this.props;

    this.isDiscovering = true;
    this.discoverError = null;

    $mcpServers
      .discoverTools({ auth_type: MCP_AUTH_TYPE.OAUTH, server_id: model.get('id') })
      .then(
        action((response) => {
          const tools = response?.tools || [];
          const initialSetup = !!this.provisionalServerId;
          this.setDiscoveredTools(tools);
          this.enabledToolNames = new Set(
            tools.filter((t) => initialSetup || this.enabledToolNames.has(t.name)).map((t) => t.name)
          );
          this.isDiscovering = false;
        })
      )
      .catch(
        action((err) => {
          this.discoverError = err.message || 'Failed to discover tools after connecting';
          this.isDiscovering = false;
        })
      );
  };

  // --- Tools ---

  @action
  setDiscoveredTools = (tools) => {
    if (!isEqual(tools, toJS(this.discoveredTools))) {
      this.discoveredTools = tools;
    }
  };

  @action
  handleToggleTool = (toolName, enabled) => {
    if (enabled) {
      this.enabledToolNames.add(toolName);
    } else {
      this.enabledToolNames.delete(toolName);
    }

    this.enabledToolNames = new Set(this.enabledToolNames);
  };

  buildEnabledTools = () => {
    const { model } = this.props;
    const existingApprovalMode = {};

    (model.get('enabled_tools') || []).forEach((tool) => {
      existingApprovalMode[tool.name] = tool.approvalMode;
    });

    return (
      this.discoveredTools
        .filter((tool) => this.enabledToolNames.has(tool.name))
        // TODO(DEFERRED): Per-tool approval mode UI. Needs sub-agent approval flow built first.
        .map((tool) => ({ name: tool.name, approvalMode: existingApprovalMode[tool.name] || 'default' }))
    );
  };

  // --- Form lifecycle ---

  @action
  handleAuthTypeChange = (value) => {
    this.authType = value;

    if (this.form) {
      this.form.setValue('auth_type', value);
    }
  };

  @action
  handleToggleAdvancedOAuth = () => {
    this.showAdvancedOAuth = !this.showAdvancedOAuth;
  };

  handleFormLoad = (form) => {
    this.form = form;
    this.authType = form.getValue('auth_type') || MCP_AUTH_TYPE.HEADERS;
    this.urlValue = form.getValue('url') || '';
    this.updateCredentialOptions();
  };

  // --- Credentials ---

  @action
  handleOpenCredentialDialog = () => {
    const { $credentials } = this.props;

    this.credentialDialogModel = $credentials.groupedCollection.forge();
    this.showCredentialDialog = true;
  };

  @action
  handleCloseCredentialDialog = () => {
    this.showCredentialDialog = false;
  };

  handleCredentialSaved = (credentialName) => {
    this.updateCredentialOptions();

    if (credentialName) {
      this.form.setValue('credential_name', credentialName);
    }
  };

  updateCredentialOptions = () => {
    if (!this.form) {
      return;
    }

    const { $credentials } = this.props;
    const options = $credentials.getCredentialOptionsByTypes(['mcp_http_headers']);
    const allOptions = [{ label: 'None', value: '' }, ...options];
    const currentValue = this.form.getValue('credential_name');

    if (currentValue && !allOptions.some((option) => option.value === currentValue)) {
      allOptions.push({ label: currentValue, value: currentValue });
    }

    this.form.getField('credential_name').setOptions(allOptions);
  };

  get callbackUrl() {
    return this.oauthDiscovered?.redirect_uri || this.serverRedirectUri;
  }

  get isOAuth() {
    return this.authType === MCP_AUTH_TYPE.OAUTH;
  }

  // --- Submit ---

  onSubmit = (form, values) => {
    const { model } = this.props;
    const clientId = values.oauth_client_id;
    // New scopes go through the OAuth setup, which registers the app again where the provider registers apps.
    const { scopesChanged } = this;

    const saveValues = {
      ...omit(values, ['oauth_client_id', ...DISCOVERED_ENDPOINT_FIELDS, ...(scopesChanged ? ['oauth_scopes'] : [])]),
      discovered_tools: this.discoveredTools,
      enabled_tools: this.buildEnabledTools()
    };

    const hasNewCredentials = clientId && clientId !== model.get('oauth_client_id');

    return model.save(saveValues, { toast: true }).then(() => {
      this.provisionalServerId = null;

      if (this.isOAuth && (hasNewCredentials || scopesChanged)) {
        return this.configureOAuth(model.get('id'));
      }

      return undefined;
    });
  };

  handleCancel = () => {
    const { model, onClose } = this.props;

    if (this.provisionalServerId) {
      model.destroy({ toast: false });
    }

    onClose();
  };

  // --- Render ---

  renderOAuthSection() {
    if (!this.isOAuth) {
      return null;
    }

    const { $auth } = this.props;
    // Connecting writes the OAuth config back to the server, so it needs the same permission a save does.
    const canConfigure = $auth.hasRbacPermissions(['ai.mcp_servers::update']);

    return (
      <>
        <Callout intent={this.oauthConnected ? 'success' : 'primary'} mb={2} icon="lock">
          <Flex alignItems="center" justifyContent="space-between" gap={1}>
            <Text>
              {this.oauthConnected
                ? 'Your account is connected to this server.'
                : 'Connect your account to load this server’s tools.'}
            </Text>
            <Button
              small
              intent="primary"
              onClick={this.handleConnect}
              loading={this.isConnecting}
              disabled={this.isConnecting || !canConfigure}
              title={canConfigure ? undefined : 'Requires permission to edit MCP servers'}
            >
              {this.oauthConnected ? 'Reconnect' : 'Connect'}
            </Button>
          </Flex>
          {this.authorizeUrl && (
            <Text small mt={1}>
              Popup blocked.{' '}
              <a href={this.authorizeUrl} target="_blank" rel="noopener noreferrer">
                Open the authorization page
              </a>
              .
            </Text>
          )}
        </Callout>

        <Box mb={2}>
          <Flex
            alignItems="center"
            gap="4px"
            style={{ cursor: 'pointer', userSelect: 'none' }}
            onClick={this.handleToggleAdvancedOAuth}
            mb={this.showAdvancedOAuth ? 1 : 0}
          >
            <Icon icon={this.showAdvancedOAuth ? 'caret-down' : 'caret-right'} color="muted" />
            <Text small muted>
              Advanced OAuth Configuration
            </Text>
          </Flex>
          <Collapse isOpen={this.showAdvancedOAuth}>
            <FlexColumn gap={0} pl={2}>
              <Callout mb={2}>
                <Flex alignItems="center" gap={1}>
                  <FlexColumn flex={1} minWidth={0}>
                    <Text small muted>
                      Providers that cannot register automatically need this callback URL
                    </Text>
                    <Text monospace fontSize={12} style={{ wordBreak: 'break-all' }}>
                      {this.callbackUrl}
                    </Text>
                  </FlexColumn>
                  <CopyToClipboardButton text={this.callbackUrl}>
                    <Button icon="clipboard" minimal small flexShrink={0} title="Copy callback URL" />
                  </CopyToClipboardButton>
                </Flex>
              </Callout>
              <Field name="oauth_client_id" large>
                <InputGroup />
              </Field>
              <Field name="oauth_auth_url" large readOnly>
                <InputGroup />
              </Field>
              <Field name="oauth_token_url" large readOnly>
                <InputGroup />
              </Field>
              <Field name="oauth_revocation_url" large readOnly>
                <InputGroup />
              </Field>
              <Field name="oauth_scopes" large>
                <InputGroup />
              </Field>
            </FlexColumn>
          </Collapse>
        </Box>
      </>
    );
  }

  renderSettingsPanel() {
    const { $auth } = this.props;

    return (
      <FlexColumn px={2} flex={1} minHeight={0} overflow="auto">
        <Field name="name" large autoFocus>
          <InputGroup />
        </Field>
        <Field name="description" large>
          <TextArea rows="3" fill />
        </Field>
        <Flex alignItems="flex-end" gap={1} mb={2}>
          <Box flex={1}>
            <Field
              name="url"
              large
              m={0}
              showError={false}
              onChange={(field, value) => {
                this.urlValue = value || '';
              }}
            >
              <InputGroup />
            </Field>
          </Box>
          <Button
            intent="primary"
            onClick={this.handleDiscover}
            loading={this.isDiscovering}
            disabled={this.isDiscovering || !this.urlValue || !$auth.hasRbacPermissions(['ai.mcp_servers::create'])}
            style={{ marginBottom: 1 }}
          >
            Discover
          </Button>
          {this.discoveredTools.length > 0 && (
            <Tag minimal style={{ marginBottom: 5 }}>
              {this.discoveredTools.length} tool{this.discoveredTools.length !== 1 ? 's' : ''}
            </Tag>
          )}
        </Flex>
        <Text small muted mb={2}>
          Discover connects to the server to list its tools and detect whether it needs authentication.
        </Text>

        {this.discoverError && (
          <Callout intent="danger" mb={2}>
            {this.discoverError}
          </Callout>
        )}

        {this.skippedTools.length > 0 && (
          <Callout intent="warning" mb={2}>
            {this.skippedTools.length} tool{this.skippedTools.length !== 1 ? 's were' : ' was'} skipped because the name
            is not supported: {this.skippedTools.join(', ')}
          </Callout>
        )}

        <Field name="auth_type" large onChange={(field, value) => this.handleAuthTypeChange(value)}>
          <Select />
        </Field>

        {/* Custom headers apply whether or not the server also uses OAuth. */}
        <Flex alignItems="flex-end" gap={1} mb={2}>
          <Box flex={1}>
            <Field name="credential_name" large m={0}>
              <Select menuWidth={350} />
            </Field>
          </Box>
          <Button icon="plus" minimal onClick={this.handleOpenCredentialDialog} style={{ marginBottom: 2 }}>
            New Credentials
          </Button>
        </Flex>

        {this.renderOAuthSection()}

        <Field name="type" large>
          <Select />
        </Field>
        {this.form?.getValue('type') === 'private' && (
          <Callout intent="warning" mb={2}>
            Private MCP servers require a Universal Agent proxy. This capability is not yet available.
          </Callout>
        )}
        <Box mb={3}>
          <Field name="enabled" showLabel={false}>
            <FormSwitch large switchLabel="Enabled" />
          </Field>
          <Field name="requires_approval" showLabel={false} helpText="Asked once per conversation.">
            <FormSwitch large switchLabel="Requires approval" />
          </Field>
        </Box>
        {this.showCredentialDialog && this.credentialDialogModel && (
          <CreateMcpCredentialDialog
            model={this.credentialDialogModel}
            onClose={this.handleCloseCredentialDialog}
            onSave={this.handleCredentialSaved}
          />
        )}
      </FlexColumn>
    );
  }

  renderToolsPanel() {
    return (
      <FlexColumn flex={1} minHeight={0}>
        {this.isDiscovering ? (
          <Flex alignItems="center" justifyContent="center" flex={1}>
            <Spinner size={30} />
          </Flex>
        ) : (
          <McpServerToolsList
            tools={this.discoveredTools}
            enabledToolNames={this.enabledToolNames}
            onToggleTool={this.handleToggleTool}
          />
        )}
      </FlexColumn>
    );
  }

  render() {
    const { isOpen, model } = this.props;

    return (
      <FormDialog
        fields={fields}
        entityName="MCP Server"
        isOpen={isOpen}
        model={model}
        onClose={this.handleCancel}
        formActionsProps={{
          onSubmit: this.onSubmit,
          onCancel: this.handleCancel,
          confirmSubmit: this.scopesChanged,
          confirmBodyContent: SCOPE_CHANGE_WARNING,
          submitButtonProps: { disabled: !this.hasChanges }
        }}
        formComponentProps={{ large: true, onLoad: this.handleFormLoad }}
        options={{ name: 'MCP Server Dialog Form' }}
        dialogBodyProps={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        style={{ maxWidth: 900 }}
        height="70vh"
      >
        <BorderedTabs defaultSelectedTabId="settings" tabIndent={16} mt={2} flex={1} minHeight={0}>
          <BorderedTab id="settings" title="Settings" panel={this.renderSettingsPanel()} />
          <BorderedTab
            id="tools"
            title={
              <Flex alignItems="center">
                <Box mr={1}>Tools</Box>
                {this.discoveredTools.length > 0 && (
                  <Tag small round textAlign="center" minWidth="20px">
                    {this.enabledToolNames.size}
                  </Tag>
                )}
              </Flex>
            }
            panel={this.renderToolsPanel()}
          />
        </BorderedTabs>
      </FormDialog>
    );
  }
}

export default McpServerDialog;
